import React from 'react';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import {
  GridActionsCellItem,
  GridCellParams,
  GridColDef,
  GridValidRowModel,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';

import { FDataGrid, FDataGridToolbarAction } from '../grid';
import { DirectBudgetLine, LookupItem } from './directbudget.models';

type Props = {
  rows: DirectBudgetLine[];
  costcenters: LookupItem[];
  accounts: LookupItem[];
  editable: boolean;
  saving?: boolean;
  onRowsChange: (rows: DirectBudgetLine[]) => void;
  onSave: (rows: DirectBudgetLine[]) => Promise<void>;
};

export default class DirectBudgetGrid extends React.PureComponent<Props> {

  private validate = (r: DirectBudgetLine) =>
    !r.costcentercode
      ? 'Cost Center is required.'
      : !r.accountcode
        ? 'Account is required.'
        : Number(r.requestedamount) <= 0
          ? 'Requested amount must be greater than zero.'
          : !r.justification.trim()
            ? 'Justification is required.'
            : '';

  private processRowUpdate = (
    n: GridValidRowModel,
    o: GridValidRowModel
  ): DirectBudgetLine => {
    const old = o as DirectBudgetLine;
    const qty = Number(n.quantity || 0);
    const upr = Number(n.unitprice || 0);
    const requested = Number(n.requestedamount || qty * upr || 0);
    const row: { [k: string]: any } = {
      ...old,
      ...n,
      id: old.id,
      lineid: old.lineid,
      quantity: qty,
      unitprice: upr,
      requestedamount: requested,
      _state: old._state === 'new' ? 'new' : 'dirty',
      __dirty: true,
    };
    row.__error = this.validate(row as DirectBudgetLine);
    const updated = row as DirectBudgetLine;
    this.props.onRowsChange(
      this.props.rows.map((r) => (r.id === old.id ? updated : r))
    );
    return updated;
  };

  private add = () => {
    const temp = -Date.now();
    this.props.onRowsChange([
      ...this.props.rows,
      {
        id: temp,
        lineid: 0,
        lineix: '',
        headerid: 0,
        costcenterid: 0,
        costcentercode: '',
        costcentername: '',
        accountid: 0,
        accountcode: '',
        accountname: '',
        linename: '',
        linecode: '',
        description: '',
        quantity: 1,
        unitprice: 0,
        requestedamount: 0,
        approvedamount: 0,
        justification: '',
        remarks: '',
        stamp: '',
        status: 1,
        _state: 'new',
        __dirty: true,
        __error: 'Cost Center is required.',
      },
    ]);
  };



  private delete = (row: any) => {
    const selected = this.props.rows.filter(
      r => (r as any).__selected === true,
    );

    if (selected.length === 0) return;

    this.props.onRowsChange(
      this.props.rows.map(
        r => selected.some(s => s.id === r.id)
          ? { ...r, _state: 'deleted', __dirty: true }
          : r,
      ),
    );
  };

  private markDeleted = () => {
    const selected = this.props.rows.filter(
      r => (r as any).__selected === true,
    );

    if (selected.length === 0) return;

    this.props.onRowsChange(
      this.props.rows.map(
        r => selected.some(s => s.id === r.id)
          ? { ...r, _state: 'deleted', __dirty: true }
          : r,
      ),
    );
  };

  private save = async () =>
    this.props.onSave(
      this.props.rows.filter(
        (r) => r.__dirty || r._state === 'new' || r._state === 'deleted'
      )
    );

  render() {
    
    const cols: GridColDef[] = [
      {
        field: 'costcentercode',
        headerName: 'Cost Center',
        width: 170,
        editable: true,
        type: 'singleSelect',
        valueOptions: this.props.costcenters.map((x) => ({
          value: x.code,
          label: `${x.code} — ${x.name}`,
        })),
        cellClassName: 'db-cell-input',
      },
      {
        field: 'accountcode',
        headerName: 'Account',
        width: 190,
        editable: true,
        type: 'singleSelect',
        valueOptions: this.props.accounts.map((x) => ({
          value: x.code,
          label: `${x.code} — ${x.name}`,
        })),
        cellClassName: 'db-cell-input',
      },
      {
        field: 'linename',
        headerName: 'Budget Item',
        width: 220,
        editable: true,
        cellClassName: 'db-cell-input',
      },
      {
        field: 'description',
        headerName: 'Description',
        width: 260,
        editable: true,
        cellClassName: 'db-cell-input',
      },
      {
        field: 'quantity',
        headerName: 'Qty',
        width: 90,
        type: 'number',
        editable: true,
        cellClassName: 'db-cell-input',
      },
      {
        field: 'unitprice',
        headerName: 'Unit Price',
        width: 140,
        type: 'number',
        editable: true,
        cellClassName: 'db-cell-input',
      },
      {
        field: 'requestedamount',
        headerName: 'Requested',
        width: 150,
        type: 'number',
        editable: true,
        cellClassName: 'db-cell-input',
      },
      {
        field: 'approvedamount',
        headerName: 'Approved',
        width: 150,
        type: 'number',
        editable: false,
        cellClassName: 'db-cell-computed',
      },
      {
        field: 'justification',
        headerName: 'Justification',
        width: 280,
        editable: true,
        cellClassName: 'db-cell-input',
      },
      {
        field: 'remarks',
        headerName: 'Remarks',
        width: 220,
        editable: true,
        cellClassName: 'db-cell-input',
      },
      {
        field: '__error',
        headerName: 'Validation',
        width: 230,
        editable: false,
        cellClassName: 'db-cell-error',
        renderCell: (p) =>
          p.value ? (
            <Typography variant="caption" color="error.main">
              {p.value}
            </Typography>
          ) : null,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 120,
        editable: false,
        getActions: (params) => {
          const row = params.row;

          const saving = Boolean(this.props.saving);

          const canEdit = Boolean(row.canedit);
          const canEditx = Boolean(this.props.editable);
          
          return [
            <Tooltip
              key="remove"
              title={
                canEdit
                  ? 'Remove from Budget'
                  : 'Item cannot currently be removed'
              }
            >
              <span>
                <GridActionsCellItem
                  icon={<DeleteIcon fontSize="small" />}
                  label="Remove from Budget"
                  disabled={saving || !canEdit}
                  onClick={() => this.delete?.(row)}
                />
              </span>
            </Tooltip>,
          ];
        },
      }
    ];

    const dirty = this.props.rows.filter((r) => r.__dirty).length;
    const invalid = this.props.rows.filter((r) => r.__error).length;

    // const toolbarActions: FDataGridToolbarAction[] = [];

    // if (this.props.editable) {
    //   toolbarActions.push(
    //     {
    //       key: 'add', //create-direct-budget
    //       label: 'Add Line',
    //       icon: <AddIcon />,
    //       disabled: !this.props.editable || Boolean(this.props.saving),
    //       onClick: this.add,
    //     }); 
    // }

    const toolbarActions = this.props.editable
      ? [
        {
          key: 'add',
          label: 'Add Direct',
          icon: <AddIcon fontSize="small" />,
          disabled: Boolean(this.props.saving),
          onClick: this.add,
        },
        {
          key: 'save',
          label: 'Save Draft',
          icon: <SaveIcon fontSize="small" />,
          disabled:
            dirty === 0 ||
            invalid > 0 ||
            !!this.props.saving,
          onClick: this.save,
        },
      ]
      : [];

    return (
      <FDataGrid
        rows={this.props.rows}
        columns={cols}
        getRowId={(r: DirectBudgetLine) => r.id}
        height={520}
        title="Direct Budget Lines"
        subtitle="Amber cells are planner inputs. Blue cells are computed or governed."
        selectionMode="single"
        isCellEditable={(p: GridCellParams) =>
          this.props.editable &&
          [
            'costcentercode',
            'accountcode',
            'linename',
            'description',
            'quantity',
            'unitprice',
            'requestedamount',
            'justification',
            'remarks',
          ].includes(p.field)
        }
        processRowUpdate={this.processRowUpdate}
        onProcessRowUpdateError={console.error}
        //addNew={this.props.editable ? this.add : undefined}
        saveAll={
          this.props.editable &&
          dirty > 0 &&
          invalid === 0 &&
          !this.props.saving
            ? this.save
            : undefined
        }
        toolbarActions={toolbarActions}
        getRowClassName={(p) =>
          p.row.__error
            ? 'db-row-error'
            : p.row.__dirty
              ? 'db-row-dirty'
              : 'db-row-clean'
        }
        sx={{
          '& .db-cell-input': { bgcolor: 'warning.lighter' },
          '& .db-cell-computed': {
            bgcolor: 'info.lighter',
            color: 'text.secondary',
          },
          '& .db-row-dirty': {
            boxShadow: 'inset 3px 0 0 0',
            boxShadowColor: 'warning.main',
          },
          '& .db-row-error': { bgcolor: 'error.lighter' },
        }}
      />
    );
  }
}
